import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import LockedGate from "@/components/LockedGate";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clapperboard, Loader2, Download, Sparkles, AlertTriangle, Video, CalendarPlus, Coins, Youtube, Instagram, Facebook, Twitter, Music2, Share2, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STYLES = [
  { id: "short_form", name: "Short-form (high energy)" },
  { id: "talking_head", name: "Talking-head" },
  { id: "slideshow", name: "Slideshow" },
  { id: "text_over_visuals", name: "Text-over-visuals (b-roll)" },
];
const ASPECTS = [
  { id: "9:16", name: "Vertical 9:16" },
  { id: "16:9", name: "Landscape 16:9" },
  { id: "1:1", name: "Square 1:1" },
];
const DURATIONS = [
  { id: "5", label: "5s", note: "Free" },
  { id: "10", label: "10s", note: "10 credits" },
  { id: "30", label: "30s", note: "30 credits" },
  { id: "45", label: "45s", note: "60 · premium" },
  { id: "60", label: "60s", note: "90 · premium" },
];
const CATEGORIES = [
  "Bible", "Spirituality", "Ancient History", "History", "True Crime", "Life Lessons",
  "Motivational", "Mindset", "Self-improvement", "Personal Development", "Philosophy",
  "Psychology", "Mental Health", "Health and Wellness", "Fitness", "Business", "Finance",
  "Entrepreneurship", "Marketing", "Leadership", "Real Estate", "Science", "Technology",
  "Nature", "Travel", "Food", "Cooking", "Sports", "Fashion", "Beauty", "Parenting",
  "Relationships", "Politics", "News", "Music", "Art", "Education", "Culture", "Humor",
  "Comedy", "Entertainment", "Social Media Tips", "DIY", "Productivity", "Storytelling",
  "Astrology", "Gaming", "Pets", "Cars", "Books",
];
const CAL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];
const PRESETS = [
  { label: "Bible verse", category: "Bible", style: "text_over_visuals", prompt: "A serene sunrise over calm mountains with soft golden light, evoking hope and faith, space for a Bible verse overlay" },
  { label: "True crime hook", category: "True Crime", style: "talking_head", prompt: "A dimly lit, suspenseful scene with moody shadows introducing a mysterious unsolved case, cinematic and tense" },
  { label: "Motivation", category: "Motivational", style: "short_form", prompt: "An athlete training hard at dawn, sweat and determination, fast dynamic cuts building to a triumphant moment" },
  { label: "Money tip", category: "Finance", style: "text_over_visuals", prompt: "Clean modern flat-lay of coins, charts and a phone showing growth, bright and trustworthy, room for a finance tip" },
  { label: "Workout", category: "Fitness", style: "short_form", prompt: "High-energy home workout montage, quick reps and dynamic angles, vibrant and motivating" },
  { label: "History fact", category: "History", style: "slideshow", prompt: "Ancient ruins and artifacts revealed with elegant transitions, epic and educational atmosphere" },
  { label: "Life lesson", category: "Life Lessons", style: "text_over_visuals", prompt: "A quiet reflective moment by a window with warm light, calm and thoughtful, space for a short life lesson" },
  { label: "Business", category: "Business", style: "talking_head", prompt: "A confident entrepreneur in a sleek modern office speaking to camera, professional and inspiring" },
];
const PUBLISH_TARGETS = [
  { id: "youtube", name: "YouTube", icon: Youtube, url: "https://www.youtube.com/upload" },
  { id: "tiktok", name: "TikTok", icon: Music2, url: "https://www.tiktok.com/tiktokstudio/upload" },
  { id: "instagram", name: "Instagram", icon: Instagram, url: "https://www.instagram.com/" },
  { id: "facebook", name: "Facebook", icon: Facebook, url: "https://www.facebook.com/reels/create" },
  { id: "x", name: "X / Twitter", icon: Twitter, url: null },
];

const StatusBadge = ({ s }) => {
  const map = { queued: ["Queued", "bg-white/10 text-muted-foreground"], processing: ["Generating…", "bg-primary/15 text-primary"], completed: ["Ready", "bg-primary/15 text-primary"], failed: ["Failed", "bg-secondary/15 text-secondary"] };
  const [label, cls] = map[s] || map.queued;
  return <Badge className={`${cls} border-0`}>{label}</Badge>;
};

const VideoStudio = () => {
  const { user } = useAuth();
  const locked = user && user.role !== "admin" && user.subscription_status !== "active";
  const [style, setStyle] = useState("short_form");
  const [category, setCategory] = useState("none");
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [duration, setDuration] = useState("5");
  const [starting, setStarting] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [credits, setCredits] = useState(null);
  const pollRef = useRef(null);
  const [sched, setSched] = useState(null);
  const [when, setWhen] = useState("");
  const [schedPf, setSchedPf] = useState(user?.platforms?.[0]?.platform || "tiktok");
  const [scheduling, setScheduling] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buying, setBuying] = useState("");
  const [pub, setPub] = useState(null);
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");

  const loadJobs = useCallback(async () => {
    if (locked) return;
    try { const r = await api.get("/grow/video/jobs"); setJobs(r.data.jobs); } catch { /* ignore */ }
  }, [locked]);
  const loadCredits = useCallback(async () => {
    if (locked) return;
    try { const r = await api.get("/grow/video/credits"); setCredits(r.data); } catch { /* ignore */ }
  }, [locked]);

  useEffect(() => { loadJobs(); loadCredits(); }, [loadJobs, loadCredits]);

  useEffect(() => {
    const pending = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (pending && !pollRef.current) pollRef.current = setInterval(loadJobs, 4000);
    else if (!pending && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current && !jobs.some((j) => j.status === "queued" || j.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs, loadJobs]);

  const COST_FALLBACK = { "5": 0, "10": 10, "30": 30, "45": 60, "60": 90 };
  const cost = credits?.costs?.[duration] ?? COST_FALLBACK[duration] ?? 0;

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the video you want to create"); return; }
    setStarting(true);
    try {
      const body = { style, prompt, aspect_ratio: aspect, duration, category: category === "none" ? "" : category };
      const r = await api.post("/grow/video/generate", body);
      if (r.data.refill) toast.success(`Auto-refill added ${r.data.refill.added} credits (demo).`);
      toast.success(cost > 0 ? `Generation started — ${cost} credits used.` : "Generation started (free 5s clip).");
      setPrompt("");
      await Promise.all([loadJobs(), loadCredits()]);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402) { toast.error("Not enough credits — top up to make this video."); setBuyOpen(true); }
      else toast.error(formatApiError(e.response?.data?.detail) || "Could not start generation");
    } finally { setStarting(false); }
  };

  const buy = async (packId) => {
    setBuying(packId);
    try {
      const r = await api.post("/grow/video/credits/purchase", { pack_id: packId });
      toast.success(`Added ${r.data.added} credits (demo — no real charge).`);
      await loadCredits();
      setBuyOpen(false);
    } catch { toast.error("Could not add credits"); }
    finally { setBuying(""); }
  };

  const toggleAutorefill = async (enabled) => {
    setCredits((c) => ({ ...c, autorefill: enabled }));
    try { await api.post("/grow/video/credits/autorefill", { enabled, pack_id: credits?.autorefill_pack || "medium" }); }
    catch { toast.error("Could not update auto-refill"); loadCredits(); }
  };

  const download = (j) => {
    const a = document.createElement("a");
    a.href = `${BACKEND}${j.video_url}?download=1`; a.download = `cloutrise-${j.id}.mp4`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const scheduleVideo = async () => {
    if (!when) { toast.error("Pick a date & time"); return; }
    setScheduling(true);
    try {
      await api.post("/calendar", { platform: schedPf, title: sched.prompt,
        notes: `🎬 AI-generated video — ready to post from Video Studio.\nStyle: ${sched.style?.replace(/_/g, " ")} · ${sched.aspect_ratio} · ${sched.duration}s`,
        scheduled_at: new Date(when).toISOString() });
      toast.success("Added to your calendar!");
      setSched(null); setWhen("");
    } catch { toast.error("Could not schedule"); }
    finally { setScheduling(false); }
  };

  const openPublish = (j) => { setPub(j); setCaption(j.prompt || ""); setHashtags(""); };
  const publishText = () => {
    const tags = hashtags.split(/[\s,]+/).filter(Boolean).map((t) => "#" + t.replace(/^#/, "")).join(" ");
    return `${caption}${tags ? "\n\n" + tags : ""}`.trim();
  };
  const publishTo = (t) => {
    const text = publishText();
    navigator.clipboard.writeText(text).catch(() => {});
    download(pub);
    const url = t.id === "x" ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}` : t.url;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Caption copied & video downloaded — finish the post on ${t.name}.`);
  };

  if (locked) {
    return <LockedGate testid="video-locked" title="Unlock the AI Video Studio"
      blurb="Turn a text prompt into short-form videos, talking-head clips, slideshows and cinematic b-roll — then download or publish them anywhere." />;
  }

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Clapperboard className="w-5 h-5" /></div>
          <h1 className="font-heading text-3xl font-black tracking-tight">AI Video Studio</h1>
        </div>
        <p className="text-muted-foreground mb-6">Pick a theme, describe your idea, and let AI generate a ready-to-post video.</p>

        {/* Credits bar */}
        {credits && (
          <div className="rounded-xl border border-primary/25 bg-primary/5 p-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="video-credits-bar">
            <div className="flex items-center gap-2"><Coins className="w-4 h-4 text-primary" />
              {credits.is_admin ? (
                <span className="text-sm font-semibold">Admin · unlimited</span>
              ) : (
                <span className="text-sm"><b className="text-primary" data-testid="video-free-remaining">{credits.free_remaining}</b> free sec left this month · <b data-testid="video-balance">{credits.balance}</b> credits</span>
              )}
            </div>
            {!credits.is_admin && (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <Switch checked={!!credits.autorefill} onCheckedChange={toggleAutorefill} data-testid="video-autorefill-toggle" />
                  <span className="text-muted-foreground">Auto-refill when low</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => setBuyOpen(true)} className="rounded-full border-primary/30 ml-auto" data-testid="video-buy-btn">
                  <Coins className="w-3.5 h-3.5 mr-1" /> Buy credits
                </Button>
              </>
            )}
          </div>
        )}

        {/* Low-credit nudge */}
        {credits && !credits.is_admin && (() => {
          const total = credits.monthly_free || 0;
          const lowFree = total > 0 && credits.free_remaining <= Math.max(10, Math.round(total * 0.2));
          const lowBal = (credits.balance || 0) < 30;
          if (!lowFree || !lowBal || credits.autorefill) return null;
          return (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="video-low-credit-banner">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="text-sm text-amber-100/90 flex-1 min-w-[200px]">
                You're almost out of free video seconds this month ({credits.free_remaining} left). Top up or turn on auto-refill so your next video isn't blocked.
              </span>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setBuyOpen(true)} className="rounded-full bg-amber-500 text-background font-bold hover:bg-amber-400" data-testid="video-low-credit-buy">Buy credits</Button>
                <Button size="sm" variant="outline" onClick={() => { toggleAutorefill(true); toast.success("Auto-refill on — we'll top you up automatically."); }} className="rounded-full border-amber-500/40 text-amber-100" data-testid="video-low-credit-autorefill">Enable auto-refill</Button>
              </div>
            </div>
          );
        })()}

        {/* Generation form */}
        <div className="rounded-xl border border-border/50 bg-card p-6 space-y-4" data-testid="video-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Topic / Theme</Label>
              <Select value={category} onValueChange={setCategory}><SelectTrigger className="h-11 bg-transparent border-white/10 mt-1" data-testid="video-category"><SelectValue placeholder="Any / freestyle" /></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="none">Any / freestyle</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs">Style</Label>
              <Select value={style} onValueChange={setStyle}><SelectTrigger className="h-11 bg-transparent border-white/10 mt-1" data-testid="video-style"><SelectValue /></SelectTrigger>
                <SelectContent>{STYLES.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs">Aspect ratio</Label>
              <Select value={aspect} onValueChange={setAspect}><SelectTrigger className="h-11 bg-transparent border-white/10 mt-1" data-testid="video-aspect"><SelectValue /></SelectTrigger>
                <SelectContent>{ASPECTS.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Duration</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-1.5" data-testid="video-duration-picker">
              {DURATIONS.map((d) => (
                <button key={d.id} onClick={() => setDuration(d.id)} data-testid={`video-duration-${d.id}`}
                  className={`rounded-lg border p-3 text-left transition-colors ${duration === d.id ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/25"}`}>
                  <div className="font-heading font-bold">{d.label}</div>
                  <div className={`text-[11px] ${d.id === "5" ? "text-primary" : "text-muted-foreground"}`}>{d.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Quick starts</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="video-presets">
              {PRESETS.map((p) => (
                <button key={p.label} onClick={() => { setCategory(p.category); setStyle(p.style); setPrompt(p.prompt); }}
                  className="rounded-full border border-white/10 hover:border-primary/40 hover:text-primary px-3 py-1.5 text-xs transition-colors" data-testid={`video-preset-${p.category.toLowerCase().replace(/\s/g, "-")}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. A barista pouring latte art in a cozy morning cafe, close-up, warm light"
            className="bg-transparent border-white/10 min-h-[90px]" data-testid="video-prompt" />
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={generate} disabled={starting} className="h-11 rounded-full bg-primary text-background font-bold px-6" data-testid="video-generate-btn">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate {cost > 0 ? `· ${cost} credits` : "· Free"}</>}
            </Button>
            <p className="text-xs text-muted-foreground">Renders in ~1–3 min. 45s & 60s stitch multiple clips (premium). You'll get a notification when it's ready.</p>
          </div>
        </div>

        {/* Gallery */}
        <div className="mt-8">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2"><Video className="w-4 h-4 text-primary" /> Your videos</h2>
          {jobs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-12 text-center text-muted-foreground" data-testid="video-empty">No videos yet — generate your first one above.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {jobs.map((j) => (
                <motion.div key={j.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-border/50 bg-card p-4 space-y-3" data-testid={`video-job-${j.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium line-clamp-1">{j.prompt}</div>
                    <StatusBadge s={j.status} />
                  </div>
                  <div className="text-xs text-muted-foreground capitalize">{j.style?.replace(/_/g, " ")} · {j.aspect_ratio} · {j.duration}s{j.category ? ` · ${j.category}` : ""}</div>
                  {j.status === "completed" && j.video_url && (
                    <>
                      <video src={`${BACKEND}${j.video_url}`} controls playsInline className="w-full rounded-lg bg-black" data-testid={`video-player-${j.id}`} />
                      <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => download(j)} size="sm" className="rounded-full bg-primary text-background font-bold" data-testid={`video-download-${j.id}`}><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                        <Button onClick={() => openPublish(j)} size="sm" variant="outline" className="rounded-full border-white/15" data-testid={`video-publish-${j.id}`}><Share2 className="w-3.5 h-3.5 mr-1" /> Publish</Button>
                        <Button onClick={() => { setSched(j); setWhen(""); }} size="sm" variant="outline" className="rounded-full border-white/15" data-testid={`video-schedule-${j.id}`}><CalendarPlus className="w-3.5 h-3.5 mr-1" /> Schedule</Button>
                      </div>
                    </>
                  )}
                  {(j.status === "queued" || j.status === "processing") && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /> Rendering your video…</div>
                  )}
                  {j.status === "failed" && (
                    <div className="rounded-lg bg-secondary/10 border border-secondary/30 p-3 text-xs text-secondary flex gap-2" data-testid={`video-error-${j.id}`}>
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{j.error || "Generation failed. Please try again."}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Schedule dialog */}
      <Dialog open={!!sched} onOpenChange={(o) => !o && setSched(null)}>
        <DialogContent className="bg-card border-border" data-testid="video-schedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">Schedule this video</DialogTitle>
            <DialogDescription>Add it to your content calendar so it goes from idea to plan.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="text-sm"><span className="text-muted-foreground">Video:</span> <b>{sched?.prompt}</b></div>
            <div><Label className="text-sm">Platform</Label>
              <Select value={schedPf} onValueChange={setSchedPf}>
                <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="video-schedule-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{CAL_PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">When to publish</Label>
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="video-schedule-datetime" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={scheduleVideo} disabled={scheduling} className="rounded-full bg-primary text-background font-bold" data-testid="video-schedule-save">
              {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add to calendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy credits dialog */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="bg-card border-border" data-testid="video-buy-dialog">
          <DialogHeader><DialogTitle className="font-heading">Buy video credits</DialogTitle>
            <DialogDescription>1 credit = 1 second of video. {credits?.plan === "growth" && "Growth plan: +10% bonus."} {credits?.plan === "pro" && "Pro plan: +20% bonus."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            {(credits?.packs || []).map((p) => (
              <button key={p.id} onClick={() => buy(p.id)} disabled={!!buying} data-testid={`video-pack-${p.id}`}
                className="w-full rounded-lg border border-white/10 hover:border-primary/40 p-4 flex items-center justify-between transition-colors disabled:opacity-50">
                <div className="text-left">
                  <div className="font-heading font-bold">{p.label}</div>
                  <div className="text-xs text-primary">{p.bonus_seconds} credits</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">${p.price}</span>
                  {buying === p.id && <Loader2 className="w-4 h-4 animate-spin" />}
                </div>
              </button>
            ))}
          </div>
          <div className="rounded-lg bg-white/5 border border-white/10 p-3 text-xs text-muted-foreground flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 shrink-0" /> Demo mode — credits are added instantly with no real charge yet.
          </div>
        </DialogContent>
      </Dialog>

      {/* Publish dialog */}
      <Dialog open={!!pub} onOpenChange={(o) => !o && setPub(null)}>
        <DialogContent className="bg-card border-border" data-testid="video-publish-dialog">
          <DialogHeader><DialogTitle className="font-heading">Publish your video</DialogTitle>
            <DialogDescription>Add a caption & hashtags, then post to any platform. We copy your caption and download the clip so you can drop it in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div><Label className="text-xs">Caption</Label>
              <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} className="bg-transparent border-white/10 mt-1 min-h-[70px]" data-testid="video-publish-caption" />
            </div>
            <div><Label className="text-xs">Hashtags</Label>
              <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="fitness motivation gymtok" className="h-11 bg-transparent border-white/10 mt-1" data-testid="video-publish-hashtags" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PUBLISH_TARGETS.map((t) => (
                <Button key={t.id} onClick={() => publishTo(t)} variant="outline" className="rounded-lg border-white/15 justify-start" data-testid={`video-publish-to-${t.id}`}>
                  <t.icon className="w-4 h-4 mr-2 text-primary" /> {t.name}
                </Button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">Tip: X/Twitter opens with your caption pre-filled. For the others, paste the copied caption and upload the downloaded clip.</p>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
};

export default VideoStudio;
